import { ApiState } from '@/models/generics';
import {
	openLocationSelector,
	closeLocationSelector,
	TLocation,
	selectLocation,
	locationLoading,
	locationSuccess,
	locationFailure,
} from '@/store/location/locationSlice';
import { locationService } from '@/services/location';
import { useUser } from './useUser';
import { useCart as unlmtdUseCart } from './unlmtd/useCart';
import { useCart } from './useCart';
import { useNotification } from './useNotification';
import { NotificationSeverity } from '@/components/NotifyToast';
import { useRouter } from 'next/router';
import {
	getCityFromUrl,
	getLocationFromLocalStorage,
	saveLocationOnLocalStorage,
} from '@/utils/location';
import usePlan from './unlmtd/usePlan';
import { useLencoDispatch } from './useLencoDispatch';
import { useLencoSelector } from './useLencoSelector';
import { dataLayerPush } from '@/utils/dataLayer';

type TUseLocation = {
	/** @deprecated */
	pincode?: number;
	/** @deprecated */
	cityId?: number;
	/** @deprecated */
	cityName?: string;
	selectedLocation: TLocation;
	selectedLocationSyncState: ApiState;
	locationActions: {
		triggerCitySelector: (isOpen: boolean) => void;
		updateSelectedLocation: (location: TLocation) => void;
		updateSelectedLocationAndNotify: (location: TLocation) => void;
		getPincodeDetails: (pincode: number) => void;
		readUserSelectedLocationFromStorage: () => Promise<TLocation>;
		saveUserSelectedLocation: (
			location: TLocation | undefined
		) => Promise<TLocation | undefined>;
	};
	isLocationSelectorOpen: boolean;
};

export function useLocation(): TUseLocation {
	const router = useRouter();
	const dispatch = useLencoDispatch();
	const {
		selectedLocation,
		selectedLocationSyncState,
		isLocationSelectorOpen,
	} = useLencoSelector(selectLocation);

	const {
		userActions: { getUser },
	} = useUser();

	const {
		cartActions: { getCart },
	} = useCart();

	const {
		cartActions: { fetchCart, clearCart },
	} = unlmtdUseCart();

	const { actions } = useNotification();
	const { selectedPlanId } = usePlan();

	async function readUserSelectedLocationFromStorage(): Promise<TLocation> {
		try {
			dispatch(locationLoading());
			const location = getLocationFromLocalStorage();
			dataLayerPush({ city_id: location.cityId });
			dispatch(locationSuccess(location));
			return location;
		} catch (error) {
			dispatch(locationFailure());
			throw error;
		}
	}

	async function saveUserSelectedLocation(
		location: TLocation | undefined
	): Promise<TLocation | undefined> {
		try {
			dispatch(locationLoading());
			saveLocationOnLocalStorage(location);
			if (location) {
				dispatch(locationSuccess(location));
			}
			return location;
		} catch (error) {
			dispatch(locationFailure());
			throw error;
		}
	}

	function updateSelectedLocation(location: TLocation) {
		saveUserSelectedLocation(location);
		clearCart();

		if (location?.pincode !== selectedLocation?.pincode) {
			getUser();
			if (window.location.pathname.includes('unlmtd')) {
				if (selectedPlanId) {
					fetchCart();
				}
			} else {
				getCart();
			}
		}

		const pathCity = getCityFromUrl();
		if (pathCity && typeof window !== 'undefined') {
			const updatedUrl = window.location.href.replaceAll(
				pathCity,
				location.cityName.toLowerCase()
			);
			router.replace(updatedUrl);
		}
		// planActions.getPlans();
	}

	function updateSelectedLocationAndNotify(location: TLocation) {
		updateSelectedLocation(location);

		actions.notifyUser({
			message: 'Location has been updated',
			severity: NotificationSeverity.SUCCESS,
		});
	}

	function getPincodeDetails(pincode: number) {
		return locationService.getPincodeServiceabilityService(pincode);
	}

	const triggerCitySelector = (isOpen: boolean) => {
		if (isOpen) {
			dispatch(openLocationSelector());
		} else {
			dispatch(closeLocationSelector());
		}
	};

	return {
		isLocationSelectorOpen,
		selectedLocation,
		...selectedLocation,
		selectedLocationSyncState,
		locationActions: {
			triggerCitySelector,
			updateSelectedLocation,
			updateSelectedLocationAndNotify,
			getPincodeDetails,
			readUserSelectedLocationFromStorage,
			saveUserSelectedLocation,
		},
	};
}
